import dayjs from 'dayjs';

// 提取 AI 点数灰度开关。
// 这里同时兼容两种来源：
// 1. 用户信息接口直接返回 `Layout:aiPoints`
// 2. 外层以 `grayscale` 对象形式传入灰度集合
export const normalizeFhAiPointsGrayValue = (value) => {
  if (value === true || value === 1) return true;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  }

  return false;
};

// 是否开启 AI 点数灰度。
// 这里只接受 1 / true / '1' / 'true' 为开启，避免把 '0'、'false' 这类字符串误判成 truthy。
export const isFhAiPointsGrayEnabled = (value) => {
  return normalizeFhAiPointsGrayValue(value);
};

export const extractFhAiPointsGrayscale = (source = {}) => {
  const grayscale = typeof source?.grayscale === 'object' && source?.grayscale !== null ? { ...source.grayscale } : {};

  if (Object.prototype.hasOwnProperty.call(grayscale, 'Layout:aiPoints')) {
    grayscale['Layout:aiPoints'] = normalizeFhAiPointsGrayValue(grayscale['Layout:aiPoints']);
  }

  // 兼容用户信息接口直接返回 aiPoints 灰度，以及外层传入 grayscale 对象两种结构。
  if (Object.prototype.hasOwnProperty.call(source, 'Layout:aiPoints')) {
    grayscale['Layout:aiPoints'] = normalizeFhAiPointsGrayValue(source['Layout:aiPoints']);
  }

  return grayscale;
};

// 部分列表字段可能是 JSON 字符串，这里做一层轻量解析，失败时保留原值。
const tryParseJson = (value) => {
  if (typeof value !== 'string') return value;

  const text = value.trim();

  if (!text || !['[', '{'].includes(text[0])) return value;

  try {
    return JSON.parse(text);
  } catch {
    return value;
  }
};

// 判断当前列表列是否命中 AI 点数“重试”按钮的字段规则。
export const isFhAiPointsTargetField = (entity, fieldKey, rules = []) => {
  if (entity == null || !fieldKey || !Array.isArray(rules) || !rules.length) {
    return false;
  }

  return rules.some((rule) => String(rule?.entity) === String(entity) && rule?.fieldKey === fieldKey);
};

// 从列表行数据里提取 requestid。
// 目前 requestid 来源于指定 fieldKey 对应的单元格原始值，字符串会先 trim。
export const getFhAiPointsRequestId = (row = {}, requestFieldKey = '') => {
  if (!requestFieldKey) return '';

  const value = row?.[requestFieldKey];

  if (typeof value === 'string') {
    // 列表数据里 requestid 可能带前后空格，统一 trim 后再参与展示判断和接口调用。
    return value.trim();
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return '';
};

const normalizeFhAiPointsNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const text = value.trim();

    if (!text) return null;

    const parsed = Number(text.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeFhAiPointsDate = (value) => {
  if (value == null || value === '') return null;

  const parsedValue = tryParseJson(value);

  if (parsedValue !== value) {
    return normalizeFhAiPointsDate(parsedValue);
  }

  if (Array.isArray(value)) {
    return value.length ? normalizeFhAiPointsDate(value[0]) : null;
  }

  if (typeof value === 'object') {
    if (value instanceof Date) {
      const date = dayjs(value);
      return date.isValid() ? date : null;
    }

    return null;
  }

  const date = dayjs(value);
  return date.isValid() ? date : null;
};

// 重试接口调用前的业务校验。
// 1. 生效时间不能晚于等于到期时间
// 2. 充值点数需要大于 0
export const validateFhAiPointsRetryData = (row = {}, validateFieldConfig = {}) => {
  const effectiveAt = normalizeFhAiPointsDate(row?.[validateFieldConfig?.effectiveAtFieldKey]);
  const expiryAt = normalizeFhAiPointsDate(row?.[validateFieldConfig?.expiryAtFieldKey]);
  const rechargeAmount = normalizeFhAiPointsNumber(row?.[validateFieldConfig?.rechargeAmountFieldKey]);

  if (effectiveAt && expiryAt && (effectiveAt.isAfter(expiryAt) || effectiveAt.isSame(expiryAt))) {
    return {
      valid: false,
      message: '生效时间不能晚于等于到期时间'
    };
  }

  if (rechargeAmount != null && rechargeAmount <= 0) {
    return {
      valid: false,
      message: '充值点数需要大于0'
    };
  }

  return {
    valid: true,
    message: ''
  };
};

const isFhAiPointsFormField = (item = {}, fieldKey = '') => {
  return String(item?.type) === '20' && item?.setting?.fieldKey === fieldKey;
};

const getFhAiPointsFormFieldValue = (formData = [], fieldKey = '') => {
  if (!Array.isArray(formData) || !fieldKey) return undefined;

  const findInModules = (modules = []) => {
    for (const item of modules) {
      // 表单模块里的 type 既可能是数字 20，也可能是字符串 '20'，这里统一兼容。
      if (isFhAiPointsFormField(item, fieldKey)) {
        return item?.fieldValue;
      }

      if (Array.isArray(item?.modules) && item.modules.length) {
        const nestedValue = findInModules(item.modules);

        if (nestedValue !== undefined) {
          return nestedValue;
        }
      }
    }

    return undefined;
  };

  for (const section of formData) {
    const fieldValue = findInModules(section?.modules || []);

    if (fieldValue !== undefined) {
      return fieldValue;
    }
  }

  return undefined;
};

// 新建/编辑/复制场景的表单提交校验。
// 复用重试场景的业务规则，避免列表和表单出现两套判断标准。
export const validateFhAiPointsFormData = (formData = [], validateFieldConfig = {}) => {
  const effectiveAtFieldKey = validateFieldConfig?.effectiveAtFieldKey;
  const expiryAtFieldKey = validateFieldConfig?.expiryAtFieldKey;
  const rechargeAmountFieldKey = validateFieldConfig?.rechargeAmountFieldKey;

  return validateFhAiPointsRetryData(
    {
      [effectiveAtFieldKey]: getFhAiPointsFormFieldValue(formData, effectiveAtFieldKey),
      [expiryAtFieldKey]: getFhAiPointsFormFieldValue(formData, expiryAtFieldKey),
      [rechargeAmountFieldKey]: getFhAiPointsFormFieldValue(formData, rechargeAmountFieldKey)
    },
    validateFieldConfig
  );
};


const normalizeFhAiPointsSelectOptions = (fieldValue) => {
  const normalized = [];
  const walk = (value) => {
    if (value == null || value === '') return;

    const parsedValue = tryParseJson(value);

    if (parsedValue !== value) {
      walk(parsedValue);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    if (typeof value === 'object') {
      normalized.push({
        label: typeof value?.label === 'string' ? value.label.trim() : '',
        value: value?.value == null ? '' : String(value.value).trim()
      });
      return;
    }

    const text = String(value).trim();

    if (!text) return;

    normalized.push({
      label: text,
      value: text
    });
  };

  walk(fieldValue);
  return normalized;
};

// 判断“充值结果”是否已经是失败态。
// 兼容两种形态：
// 1. 原始 option value，例如 5Jhe7D
// 2. 列表已经格式化后的展示值，例如 “失败”
// 只有命中失败态时才允许展示“重试”按钮。
export const isFhAiPointsRetryResultFailure = (row = {}, resultFieldConfig = {}) => {
  const fieldKey = resultFieldConfig?.fieldKey;

  if (!fieldKey) return false;

  const failureValue = resultFieldConfig?.failureOption?.value == null ? '' : String(resultFieldConfig.failureOption.value).trim();
  const failureText = typeof resultFieldConfig?.failureOption?.text === 'string' ? resultFieldConfig.failureOption.text.trim() : '';
  const currentOptions = normalizeFhAiPointsSelectOptions(row?.[fieldKey]);

  if ((!failureValue && !failureText) || !currentOptions.length) return false;

  return currentOptions.some((item) => {
    return (failureValue && item.value === failureValue) || (failureText && [item.label, item.value].includes(failureText));
  });
};
